<template lang="pug">
    .sub-card(ref="card" :class="{ 'is-placeholder': isPlaceholderState }")
        .level.sub-header.is-mobile(v-if="isPlaceholderState")
            .level-left
                input.input(type="text" v-model="sub.name")
            .level-right
                input.input(type="color" v-model="sub.color")
        .level.sub-header.is-mobile(v-else :style="{ backgroundColor: sub.color}")
            .level-left
                .level-item.is-toggle.is-unselectable(v-if="!sub.isSystem" @click="changeStatus")
                    span.icon.is-medium(v-show="sub.status")
                        i.fas.fa-toggle-on.fa-lg.has-text-success
                    span.icon.is-medium(v-show="!sub.status")
                        i.fas.fa-toggle-off.fa-lg.has-text-danger
                .level-item
                    .title.is-5.text-overflow(ref="title" :style="{ color: fontColor }") {{ sub.name }}
            .level-right
                input.input(v-if="isPlaceholderState" type="color" v-model="sub.color")
                .dropdown(v-else :class="{ 'is-active': isMenuActive }")
                    .dropdown-trigger.is-toggle.is-unselectable(@click="isMenuActive=!isMenuActive")
                        span.icon.is-medium(:style="{ color: fontColor }")
                            i.fas.fa-ellipsis-v
                    .dropdown-menu
                        .dropdown-content
                            a.dropdown-item(@click="edit") Edit
                            template(v-if="!sub.isSystem")
                                hr.dropdown-divider
                                a.dropdown-item(@click="remove") Delete
        .sub-content
            table.table.is-fullwidth
                tbody
                    tr(v-if="!sub.isSystem")
                        td Price
                        td.has-text-right
                            span $
                            input.input(
                                v-if="isPlaceholderState"
                                type="number"
                                min="0"
                                :max="maxPrice"
                                v-model="sub.price"
                                :style="{ width: inputWidth(sub.price) }")
                            span(v-else) {{ sub.price}}
                    tr(v-if="!sub.isSystem")
                        td Days
                        td.has-text-right
                            input.input(
                                v-if="isPlaceholderState"
                                type="number"
                                min="0"
                                :max="maxDays"
                                v-model="sub.days"
                                :style="{ width: inputWidth(sub.days) }")
                            span(v-else) {{ sub.days}}
                    tr(v-if="!sub.isSystem")
                        td Try Free
                        td.has-text-right
                            input.input(
                                v-if="isPlaceholderState"
                                type="number"
                                min="0"
                                :max="maxDays"
                                v-model="sub.tryFree"
                                :style="{ width: inputWidth(sub.tryFree) }")
                            span(v-else) {{ sub.tryFree}}
                    tr(v-if="sub.isSystem")
                        td Visible
                        td.has-text-right
                            span.icon.is-medium(@click="toggleVisible" style="cursor:pointer")
                                i.fas.fa-lg(:class="sub.visible ? 'fa-toggle-on has-text-success' : 'fa-toggle-off has-text-danger'")
                    tr
                        td(colspan="2") Description
                    tr
                        td(colspan="2")
                            textarea.textarea(v-model="sub.description" :disabled="!isPlaceholderState")
                    tr
                        td(colspan="2") Key (auto)
                    tr
                        td(colspan="2")
                            input.input(:value="displayKey" disabled)
                    template(v-if="!sub.isSystem")
                        tr
                            td(colspan="2") Stripe key
                        tr
                            td(colspan="2")
                                input.input(
                                    :value="sub.stripeProductId"
                                    disabled
                                    placeholder="Created automatically")
                        tr
                            td(colspan="2") Google key
                        tr
                            td(colspan="2")
                                input.input(
                                    v-model="sub.android"
                                    :disabled="!isPlaceholderState"
                                    placeholder="Play Console product ID")
                        tr
                            td(colspan="2") iOS key
                        tr
                            td(colspan="2")
                                input.input(
                                    v-model="sub.ios"
                                    :disabled="!isPlaceholderState"
                                    placeholder="App Store Connect product ID")
                    tr(v-if="isPlaceholderState")
                        td(colspan="2") Permissions
                    tr(v-if="isPlaceholderState")
                        td(colspan="2")
                            .perm-group(v-for="group of permissionGroups" :key="group.key")
                                .perm-group-label {{ group.label }}
                                label.perm-item(
                                    v-for="key of group.permissions"
                                    :key="key"
                                    :title="permissionDescriptions[key]")
                                    input(type="checkbox" :checked="hasPerm(key)" @change="togglePerm(key)")
                                    span {{ key }}
                    tr(v-else)
                        td Permissions
                        td.has-text-right {{ enabledCount }}
                    tr(v-if="!sub.isSystem")
                        td Active
                        td.has-text-right {{ sub.active || 0 }}
                    tr(v-if="!sub.isSystem")
                        td Purchases
                        td.has-text-right {{ sub.purchases }}
                    tr(v-if="isPlaceholderState")
                        td(colspan="2")
                            .level.is-mobile
                                .level-left
                                    .level-item
                                        button.button.is-success(@click="save")
                                            span.icon
                                                i.fas.fa-check
                                            span Save
                                    .level-item
                                        a.is-link(@click="clear")
                                            span Clear
                    tr(v-else-if="!sub.isSystem")
                        td.has-text-right(colspan="2")
                            span $
                            span {{ totalSum }}
</template>

<script>
import { PERMISSION_GROUPS, PERMISSION_DESCRIPTIONS } from "@/permissions";

// B-21: room the native number spinner takes inside the field
const SPINNER_WIDTH = "1.6rem";

const defaultSub = {
  _id: null,
  name: "New sub",
  price: 0,
  days: 0,
  tryFree: 0,
  description: "",
  active: 0,
  purchases: 0,
  color: "#008C67",
  status: false,
  key: "",
  ios: "",
  android: "",
  permissions: []
};

export default {
  name: "SubCard",
  props: {
    isPlaceholder: {
      type: Boolean,
      default: false
    },
    sub: {
      type: Object,
      default() {
        return Object.assign({}, defaultSub);
      }
    }
  },
  data: () => ({
    maxPrice: 1000000,
    maxDays: 366,
    isLight: false,
    id: null,
    isMenuActive: false,
    placeholderState: false
  }),
  watch: {
    "sub.price"(price) {
      if (price > this.maxPrice) this.sub.price = this.maxPrice;
    }
  },
  computed: {
    isPlaceholderState() {
      return this.isPlaceholder || this.placeholderState;
    },
    totalSum() {
      return this.sub.price * this.sub.purchases;
    },
    // B-17: key генерируется на сервере из name; для нового плана показываем превью slug,
    // для существующего — сохранённый key. Ручной ввод убран.
    displayKey() {
      if (this.sub.key) return this.sub.key;
      return String(this.sub.name || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    },
    // A-14: каталог прав для чекбоксов
    permissionGroups: () => PERMISSION_GROUPS,
    permissionDescriptions: () => PERMISSION_DESCRIPTIONS,
    enabledCount() {
      return (this.sub.permissions || []).length;
    },
    fontColor() {
      return this.isLight ? "#000" : "#fff";
    }
  },
  methods: {
    changeStatus() {
      this.sub.status = !this.sub.status;
      this.save();
    },
    edit() {
      this.placeholderState = true;
      this.isMenuActive = false;
    },
    remove() {
      this.isMenuActive = false;
      if (!confirm(`Remove subscription "${this.sub.name}"?`)) return;
      this.$emit("save", Object.assign({}, { _id: this.sub._id }));
    },
    // A-14: чекбоксы прав
    hasPerm(key) {
      return (this.sub.permissions || []).includes(key);
    },
    togglePerm(key) {
      // иммутабельная замена: не мутируем общий defaultSub.permissions (shared ref)
      const current = Array.isArray(this.sub.permissions)
        ? this.sub.permissions
        : [];
      const next = current.includes(key)
        ? current.filter(k => k !== key)
        : [...current, key];
      this.$set(this.sub, "permissions", next);
    },
    // A-15: тумблер видимости системного статуса («Show» на макете)
    toggleVisible() {
      this.sub.visible = !this.sub.visible;
      this.save();
    },
    save() {
      let check = true;

      if (this.sub.isSystem) {
        // A-15: у системных статусов нет price/days — достаточно имени
        check = !!this.sub.name && this.sub.name !== defaultSub.name;
      } else {
        this.sub.price = Number(this.sub.price) || 0;
        this.sub.days = Number(this.sub.days) || 0;
        this.sub.tryFree = Number(this.sub.tryFree) || 0;
        this.sub.ios = (this.sub.ios || "").trim() || null;
        this.sub.android = (this.sub.android || "").trim() || null;

        Object.keys(defaultSub).some(key => {
          if (["name", "price", "days"].includes(key)) {
            if (this.sub[key] === defaultSub[key]) {
              check = false;
              return true;
            }
          }
        });
      }

      if (check) {
        this.$emit("save", Object.assign({}, this.sub));

        if (this.placeholderState) {
          this.placeholderState = false;
        } else {
          setTimeout(() => {
            this.clear();
          }, 0);
        }

        setTimeout(() => {
          this.detectColor();
        }, 0);
      } else {
        console.log("Default data");
      }
    },
    clear() {
      if (this.isPlaceholder)
        Object.keys(defaultSub).forEach(key => {
          this.sub[key] = defaultSub[key];
        });
    },
    // B-21: width must follow the input's own font-size (ch, not rem) and
    // reserve room for the native spinner, which Safari renders permanently —
    // otherwise the arrows cover the digits.
    inputWidth(l = "0") {
      let digits = Math.max(2, l.toString().length);
      return `calc(${digits}ch + ${SPINNER_WIDTH})`;
    },
    hexToRgb(hex) {
      let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? [
            parseInt(result[1], 16),
            parseInt(result[2], 16),
            parseInt(result[3], 16)
          ]
        : null;
    },
    detectColor() {
      let color = this.hexToRgb(this.sub.color);
      if (color) {
        this.isLight = color.find(c => c > 190);
      }
    }
  },
  mounted() {
    this.id = this._uid;
    this.detectColor();
  }
};
</script>

<style lang="scss" scoped>
.sub-card {
  border-radius: 5px;
  background-color: $cardFontColor;
  overflow: hidden;
  transition: all 0.3s ease-in-out;

  &.is-placeholder {
    .sub-header {
      .level-left {
        max-width: calc(100% - 3rem);
      }
    }
  }

  .table {
    background-color: transparent;
    color: $defaultColor;
    font-size: 1.2rem;
    margin-bottom: 0.5rem;

    tr {
      td {
        border: none;
        vertical-align: middle;
        padding: 0.3rem 0.75rem;

        &.has-text-right {
          font-size: 2rem;
          font-weight: bold;

          span {
            font-size: 2rem;
            line-height: 3rem;
            font-weight: bold;
          }
          .input {
            font-size: 2rem;
            height: 1.5em;
            font-weight: bold;
            text-align: right;
            color: $defaultColor;
            max-width: calc(7ch + 1.6rem);
          }
        }

        &:first-child {
          min-width: 80%;
        }

        .level {
          margin: 0.4rem 0;

          .level-item {
            margin-right: 1.5rem;
          }
        }
      }
    }
  }

  .input {
    background-color: transparent;
    border-radius: 0;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    color: transparentize($defaultColor, 0.5);
    font-size: 1.2rem;

    &[type="number"] {
      padding: 0;
    }
  }

  .textarea {
    background-color: $cardDarkBackground;
    color: $defaultColor;
  }

  // A-14: сетка чекбоксов прав
  .perm-group {
    margin-bottom: 0.6rem;

    .perm-group-label {
      font-size: 0.85rem;
      font-weight: bold;
      opacity: 0.7;
      margin-bottom: 0.2rem;
    }

    .perm-item {
      display: inline-flex;
      align-items: center;
      width: 100%;
      font-size: 0.9rem;
      cursor: pointer;
      padding: 0.1rem 0;

      input {
        margin-right: 0.4rem;
      }
    }
  }

  .sub-header {
    margin-bottom: 0.5rem;
    padding: 0.5rem;
    background-color: $cardDarkBackground;

    .is-toggle {
      flex: 0;
      padding: 0.35rem;
      cursor: pointer;
      transition: all 0.2s linear;
      border-radius: 100%;
      background-color: transparent;

      &:hover {
        background-color: rgba(0, 0, 0, 0.2);
      }
    }
    .dropdown-menu {
      left: auto;
      right: -0.5rem;
      top: calc(100% + 0.3rem);

      .dropdown-content {
        border-radius: 0;
      }
    }

    .level-left {
      max-width: calc(100% - 7rem);
      flex: 1;
      padding-right: 0.3rem;

      .level-item {
        max-width: 100%;
        justify-content: flex-start;
      }
    }
    .level-right {
      flex-basis: 2.7rem;

      .input[type="color"] {
        padding: 0.2rem 0.2rem;
        background-color: transparent;
        border: none;
        box-shadow: none;
        cursor: pointer;
      }
    }
  }
}
</style>
